import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc, deleteDoc, limit } from 'firebase/firestore';
import { batchLoadInmobiliarias } from '../../utils/batchLoadInmobiliarias';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';
import { useUser } from '../../context/UserContext';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

interface Afiliado {
  id: string;
  nombre: string;
  correo?: string;
  telefono?: string;
  calificacion?: number;
  inmobiliaria_doc_ids?: string[];
  activo?: boolean;
  creado_auto?: boolean;
  verificacion_pendiente?: boolean;
  mls_agent_key?: string;
  miembro_oficial?: boolean;
  propsActivas?: number;
  inmobiliaria?: string;
  [key: string]: any;
}

export default function AdminAfiliado() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [afiliados, setAfiliados] = useState<Afiliado[]>([]);
  const [filtro, setFiltro] = useState('');
  const [filtroInmobiliaria, setFiltroInmobiliaria] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('verificados');
  const [loading, setLoading] = useState(true);
  const [paginaActual, setPaginaActual] = useState(1);
  const afiliadosPorPagina = 15;

  useEffect(() => {
    if (!user) return;

    const fetchAfiliados = async () => {
      // Sprint 1 (F4-003): permite ADMIN y ASOCIADO. ASOCIADO queda restringido
      // a afiliados de su propia inmobiliaria via filtro server-side.
      const rol = (user as any).rol;
      const userInmo = (user as any).inmobiliaria_doc_id;
      const esAdmin = Array.isArray(user.tipo) && user.tipo.includes('admin');
      const esAsociado = rol === 'ASOCIADO';

      if (!esAdmin && !esAsociado) {
        return navigate('/');
      }

      if (esAsociado && !userInmo) {
        // ASOCIADO sin inmobiliaria asignada: denegar
        return navigate('/');
      }

      try {
        // Sprint 1 (F4-003, F8-004): query filtrada server-side + limit
        const asesorQuery = esAsociado
          ? query(
              collection(db, 'asesor'),
              where('inmobiliaria_doc_ids', 'array-contains', userInmo),
              limit(2000)
            )
          : query(collection(db, 'asesor'), limit(2000));

        const asesorSnap = await getDocs(asesorQuery);
        const asesorDocs = asesorSnap.docs.filter(d => {
          const data = d.data();
          return !data.estatus || data.estatus === 'activo';
        });

        // Batch: pre-cargar conteo de props MLS por agente
        const propsMlsSnap = await getDocs(
          query(collection(db, 'propiedades'), where('fuente', '==', 'flexmls'), where('estado', '==', 'A'))
        );
        const propsPorAgente = new Map<string, number>();
        propsMlsSnap.docs.forEach(d => {
          const ids = d.data().asesor_doc_ids;
          if (Array.isArray(ids)) {
            ids.forEach((aid: string) => propsPorAgente.set(aid, (propsPorAgente.get(aid) || 0) + 1));
          }
        });

        // Sprint 1 (F5A-001): batch loader reemplaza el for-loop N+1.
        const inmobiliariaIds: string[] = [];
        asesorDocs.forEach(d => {
          const ids = d.data().inmobiliaria_doc_ids;
          if (Array.isArray(ids) && ids.length > 0) inmobiliariaIds.push(ids[0]);
        });
        const inmoMapRaw = await batchLoadInmobiliarias(inmobiliariaIds);
        const inmobiliariaMap = new Map<string, string>();
        inmoMapRaw.forEach((v, k) => inmobiliariaMap.set(k, v?.nombre || 'Sin nombre'));

        const lista: Afiliado[] = asesorDocs.map(docSnap => {
          const data = docSnap.data();
          const inmIds = data.inmobiliaria_doc_ids;
          const inmId = Array.isArray(inmIds) && inmIds.length > 0 ? inmIds[0] : '';
          return {
            id: docSnap.id,
            ...data,
            nombre: data.nombre || '',
            correo: data.correo,
            telefono: data.telefono,
            calificacion: data.calificacion,
            creado_auto: data.creado_auto,
            verificacion_pendiente: data.verificacion_pendiente,
            mls_agent_key: data.mls_agent_key,
            miembro_oficial: data.miembro_oficial,
            propsActivas: propsPorAgente.get(docSnap.id) || 0,
            inmobiliaria: inmobiliariaMap.get(inmId) || (inmId ? 'Sin nombre' : 'Sin inmobiliaria'),
          } as Afiliado;
        });

        lista.sort((a, b) => (a.nombre || '').toLowerCase().localeCompare((b.nombre || '').toLowerCase()));
        setAfiliados(lista);
      } catch (err) {
        console.error('Error al cargar afiliados:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAfiliados();
  }, [user, navigate]);

  // Filtros
  const afiliadosFiltrados = afiliados.filter(a => {
    const matchTexto = (a.nombre || '').toLowerCase().includes(filtro.toLowerCase()) ||
                       (a.correo || '').toLowerCase().includes(filtro.toLowerCase());
    const matchInmobiliaria = filtroInmobiliaria === '' || a.inmobiliaria === filtroInmobiliaria;
    let matchTipo = true;
    if (filtroTipo === 'verificados') matchTipo = !a.verificacion_pendiente;
    else if (filtroTipo === 'pendiente') matchTipo = !!a.verificacion_pendiente;
    else if (filtroTipo === 'mls') matchTipo = !!(a.mls_agent_key || a.creado_auto);
    else if (filtroTipo === 'manual') matchTipo = !a.mls_agent_key && !a.creado_auto;
    return matchTexto && matchInmobiliaria && matchTipo;
  });

  const totalPaginas = Math.ceil(afiliadosFiltrados.length / afiliadosPorPagina);
  const startIndex = (paginaActual - 1) * afiliadosPorPagina;
  const afiliadosPaginados = afiliadosFiltrados.slice(startIndex, startIndex + afiliadosPorPagina);

  const inmobiliariasUnicas = Array.from(new Set(afiliados.map(a => a.inmobiliaria).filter(Boolean))).sort();

  // Stats
  const totalVerificados = afiliados.filter(a => !a.verificacion_pendiente).length;
  const totalPendientes = afiliados.filter(a => a.verificacion_pendiente).length;

  const handleEliminar = async (af: Afiliado) => {
    if (af.mls_agent_key || af.creado_auto) {
      Swal.fire({
        icon: 'info',
        title: 'Agente MLS',
        text: 'Este afiliado fue creado por el sync MLS. No se puede eliminar porque se recrearia en la proxima sincronizacion.',
      });
      return;
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'Eliminar afiliado?',
      html: `Se eliminara a <strong>${af.nombre}</strong>. Esta accion no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Si, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, 'asesor', af.id));
        setAfiliados(prev => prev.filter(a => a.id !== af.id));
        Swal.fire({ icon: 'success', title: 'Eliminado', text: `${af.nombre} fue eliminado.` });
      } catch (error) {
        console.error('Error eliminando afiliado:', error);
        Swal.fire('Error', 'No se pudo eliminar.', 'error');
      }
    }
  };

  if (!user) {
    return <div className="container mt-5 text-center py-5"><div className="spinner-border text-primary" role="status" /><p className="mt-3">Cargando...</p></div>;
  }

  if (!user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger text-center">
          <h4>Acceso denegado</h4>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/admin-dashboard')}>Volver</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container-fluid px-4 mt-4 mb-5">
        <Breadcrumb items={[
          { label: 'Dashboard', path: '/admin-dashboard' },
          { label: 'Afiliados' }
        ]} />

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">
            <i className="fas fa-user-tie me-2 text-primary"></i>Afiliados
          </h2>
        </div>

        {/* Stats clickeables */}
        <div className="d-flex gap-2 mb-3 flex-wrap">
          <span
            className={`badge fs-6 p-2 ${filtroTipo === 'verificados' ? 'bg-success' : 'border border-success text-success'}`}
            style={{ cursor: 'pointer' }}
            onClick={() => { setFiltroTipo('verificados'); setPaginaActual(1); }}
          >
            <i className="fas fa-check-circle me-1"></i>{totalVerificados} Verificados
          </span>
          {totalPendientes > 0 && (
            <span
              className={`badge fs-6 p-2 ${filtroTipo === 'pendiente' ? 'bg-warning text-dark' : 'border border-warning text-warning'}`}
              style={{ cursor: 'pointer' }}
              onClick={() => { setFiltroTipo(filtroTipo === 'pendiente' ? 'verificados' : 'pendiente'); setPaginaActual(1); }}
            >
              <i className="fas fa-clock me-1"></i>{totalPendientes} Pendientes
            </span>
          )}
          <span
            className={`badge fs-6 p-2 ${filtroTipo === '' ? 'bg-primary' : 'border border-primary text-primary'}`}
            style={{ cursor: 'pointer' }}
            onClick={() => { setFiltroTipo(''); setPaginaActual(1); }}
          >
            {afiliados.length} Todos
          </span>
          <span className="badge bg-secondary fs-6 p-2">
            <i className="fas fa-filter me-1"></i>{afiliadosFiltrados.length} mostrados
          </span>
        </div>

        {/* Filtros */}
        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="row g-2">
              <div className="col-md-4">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar nombre o correo..."
                  value={filtro}
                  onChange={e => { setFiltro(e.target.value); setPaginaActual(1); }}
                />
              </div>
              <div className="col-md-3">
                <select className="form-select form-select-sm" value={filtroInmobiliaria} onChange={e => { setFiltroInmobiliaria(e.target.value); setPaginaActual(1); }}>
                  <option value="">Todas las agencias</option>
                  {inmobiliariasUnicas.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <select className="form-select form-select-sm" value={filtroTipo} onChange={e => { setFiltroTipo(e.target.value); setPaginaActual(1); }}>
                  <option value="verificados">Verificados</option>
                  <option value="pendiente">Pendientes verificar</option>
                  <option value="">Todos</option>
                  <option value="mls">Solo MLS</option>
                  <option value="manual">Solo manuales</option>
                </select>
              </div>
              <div className="col-md-1">
                <button className="btn btn-outline-secondary btn-sm w-100" onClick={() => { setFiltro(''); setFiltroInmobiliaria(''); setFiltroTipo('verificados'); }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /><p className="mt-3">Cargando afiliados...</p></div>
        ) : (
          <>
            <div className="table-responsive">
              <table aria-label="Tabla de Admin Afiliado" className="table table-hover table-sm align-middle">
                <thead className="table-dark">
                  <tr>
                    <th style={{ width: '25%' }}>Afiliado</th>
                    <th scope="col">Correo</th>
                    <th scope="col">Agencia</th>
                    <th scope="col">Props MLS</th>
                    <th style={{ width: '100px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {afiliadosPaginados.length > 0 ? (
                    afiliadosPaginados.map(af => {
                      const esMls = !!(af.mls_agent_key || af.creado_auto);
                      return (
                        <tr key={af.id}>
                          <td>
                            <div className="fw-semibold">{af.nombre}</div>
                            <div className="d-flex gap-1 mt-1 flex-wrap">
                              {af.miembro_oficial && (
                                <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>
                                  <i className="fas fa-check-circle me-1"></i>Oficial
                                </span>
                              )}
                              {!af.miembro_oficial && (
                                <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                                  No oficial
                                </span>
                              )}
                              {esMls && (
                                <span className="badge bg-info" style={{ fontSize: '0.65rem' }}>
                                  <i className="fas fa-database me-1"></i>MLS
                                </span>
                              )}
                              {af.verificacion_pendiente && (
                                <span className="badge bg-warning text-dark" style={{ fontSize: '0.65rem' }}>
                                  <i className="fas fa-clock me-1"></i>Pendiente
                                </span>
                              )}
                            </div>
                          </td>
                          <td><small>{af.correo || <span className="text-muted">-</span>}</small></td>
                          <td><small>{af.inmobiliaria || 'Sin asignar'}</small></td>
                          <td>
                            <span className={`badge ${af.propsActivas && af.propsActivas > 0 ? 'bg-success' : 'bg-secondary'}`}>
                              {af.propsActivas || 0}
                            </span>
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button className="btn btn-primary" onClick={() => navigate(`/ver-admin-afiliado/${af.id}`)} title="Ver">
                                <i className="fas fa-eye"></i>
                              </button>
                              <button className="btn btn-outline-warning" onClick={() => navigate(`/editar-afiliado/${af.id}`)} title="Editar">
                                <i className="fas fa-edit"></i>
                              </button>
                              {!esMls && (
                                <button className="btn btn-outline-danger" onClick={() => handleEliminar(af)} title="Eliminar">
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        No hay afiliados que coincidan con los filtros.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginacion */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                {afiliadosFiltrados.length > 0 ? `${startIndex + 1}-${Math.min(startIndex + afiliadosPorPagina, afiliadosFiltrados.length)} de ${afiliadosFiltrados.length}` : '0 resultados'}
              </small>
              {totalPaginas > 1 && (
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPaginaActual(paginaActual - 1)} disabled={paginaActual === 1}>
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    {Array.from({ length: Math.min(7, totalPaginas) }, (_, i) => {
                      let pageNum: number;
                      if (totalPaginas <= 7) pageNum = i + 1;
                      else if (paginaActual <= 4) pageNum = i + 1;
                      else if (paginaActual >= totalPaginas - 3) pageNum = totalPaginas - 6 + i;
                      else pageNum = paginaActual - 3 + i;
                      return (
                        <li key={pageNum} className={`page-item ${paginaActual === pageNum ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPaginaActual(pageNum)}>{pageNum}</button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPaginaActual(paginaActual + 1)} disabled={paginaActual === totalPaginas}>
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          </>
        )}
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
